import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { RootReducerState } from '../../redux/Reducers';
import { additionalQueryParams } from '../../utils/utilities';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import {
  CreatePBXExtensionDto,
  PBXExtensionDidsDeleteAction,
  PBXExtensionListItemType,
  PBXExtensionType,
  ReCredentialPBXExtensionsDto,
  UpdatePBXExtensionDto,
} from '../../types/pbx-extension/pbx-extension.type';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { DeleteResultType } from '../../types/utils/delete-result.type';

export const postCheckPBXExtensionsExistence = createAsyncThunk<
  { success: { username: string; id: number }[]; errors: { username: string; message: string }[] },
  { data: { extensions: Array<{ username: string; domain: string }> } },
  { state: RootReducerState }
>('pbx-extensions/postCheckPBXExtensionsExistence', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: { username: string; id: number }[];
      errors: { username: string; message: string }[];
    }>(`${process.env.REACT_APP_API_URL}/pbx-extension/check`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting PBX Extensions!' });
  }
});

export const patchCredentialPBXExtensions = createAsyncThunk<
  { id: number },
  { data: ReCredentialPBXExtensionsDto },
  { state: RootReducerState }
>('pbx-extensions/patchCredentialPBXExtensions', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.patch<{ id: number }>(
      `${process.env.REACT_APP_API_URL}/pbx-extension/update-credentials`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating PBX Extensions!' });
  }
});

export const postPBXExtensionDetails = createAsyncThunk<
  { success: { username: string }[]; errors: { username: string; message: string }[] },
  { data: CreatePBXExtensionDto },
  { state: RootReducerState }
>('pbx-extensions/postPBXExtensionDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: { username: string }[];
      errors: { username: string; message: string }[];
    }>(`${process.env.REACT_APP_API_URL}/pbx-extension`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating PBX Extension!' });
  }
});

export const patchPBXExtensionDetails = createAsyncThunk<
  PBXExtensionType,
  { id: number; data: UpdatePBXExtensionDto },
  { state: RootReducerState }
>('pbx-extensions/patchPBXExtensionDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<PBXExtensionType>(`${process.env.REACT_APP_API_URL}/pbx-extension/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating PBX Extension!' });
  }
});

export const deletePBXExtensionDetails = createAsyncThunk<
  DeleteResultType,
  { id: number; fetchList: boolean; didsAction?: PBXExtensionDidsDeleteAction },
  { state: RootReducerState }
>('pbx-extensions/deletePBXExtensionDetails', async ({ id, fetchList, didsAction }, thunkAPI) => {
  try {
    const response = await axios.delete<DeleteResultType>(`${process.env.REACT_APP_API_URL}/pbx-extension/${id}`, {
      params: didsAction ? { dids_action: didsAction } : undefined,
    });
    if (fetchList)
      thunkAPI.dispatch(getPBXExtensionsList(thunkAPI.getState().pbxExtensionsReducer.pbxExtensionsListRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting PBX Extension!' });
  }
});

export const getPBXExtensionsList = createAsyncThunk<
  PaginationType<PBXExtensionListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('pbx-extensions/getPBXExtensionsList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;

  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };

    const response = await axios.get<PaginationType<PBXExtensionListItemType>>(
      `${process.env.REACT_APP_API_URL}/pbx-extension`,
      controller ? { signal: controller.signal, params: mergedParams } : { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching PBX Extensions!' });
  }
});

export const getPBXExtensionDetails = createAsyncThunk<PBXExtensionType, { id: number }, { state: RootReducerState }>(
  'pbx-extensions/getPBXExtensionDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<PBXExtensionType>(`${process.env.REACT_APP_API_URL}/pbx-extension/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching PBX Extension!' });
    }
  }
);
